import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";

import { TCatalogEntities } from "types";
import { VehicleStore } from "stores";

import List from "../../List";
import Filter from "../../Filter";

import GetriebeReducer from "./GetriebeReducer";
import GetriebeIndicator from "./GetriebeIndicator";

import Pipeline from "../Pipeline";
import { sortAsc } from "../helper";

interface IFilter {
  entities: TCatalogEntities;
  value: number[];
  dispatchFilter?: any;
}

export const SideFilter: React.FC<IFilter> = ({
  entities,
  value = [],
  dispatchFilter = () => null,
}): JSX.Element | null => {
  const { formatMessage: t } = useIntl();

  if (!entities.getriebe) return null;

  const items = Object.values(entities.getriebe);

  const vehiclesCount = items.length;

  const setValueHandler = (value: any) => {
    // Check if all variants are selected
    const newValue = value.length === vehiclesCount ? [] : value;
    dispatchFilter({ type: "UPDATE_FILTER", payload: { getriebe: newValue } });
  };
  const options = items.map(({ id, name }) => ({
    value: id,
    title: t({ id: `getriebe.${id}.label` }, { defaultMessage: name }),
  }));

  const all = {
    title: t({ id: "common.alle", defaultMessage: "Alle" }),
  };

  const Component = (
    <Box sx={{ width: 1 }}>
      <List {...{ value, options, setValueHandler, all }} />
    </Box>
  );

  return (
    <Filter
      component={Component}
      title={t({ id: "filter.getriebe.label", defaultMessage: "Getriebe" })}
      open={false}
    />
  );
};

export const SimpleFilter: React.FC<IFilter> = ({
  entities = {},
  value = [],
  dispatchFilter = () => null,
}): JSX.Element | null => {
  const { formatMessage: t } = useIntl();
  const { state: vehicles } = React.useContext(VehicleStore);

  const setValueHandler = (event: SelectChangeEvent) => {
    const value = parseInt(event.target.value);
    // Check if all variants are selected
    const newValue = value === 0 ? [] : [value];
    dispatchFilter({
      type: "UPDATE_FILTER",
      payload: { getriebe: newValue },
    });
  };

  if (!(entities.getriebe && vehicles)) return null;

  const items = Object.values(entities.getriebe);

  const options = items
    .map(({ id, name }) => ({
      value: id,
      title: t({ id: `getriebe.${id}.label`, defaultMessage: name }),
    }))
    .sort(sortAsc);

  const all = {
    title: t({ id: "common.alle", defaultMessage: "Alle" }),
  };

  return (
    <FormControl size="small">
      <InputLabel id="filter-getriebe-simple-label">
        <FormattedMessage
          id="filter.getriebe.label"
          defaultMessage="Getriebe"
        />
      </InputLabel>
      <Select
        labelId="filter-getriebe-simple-label"
        id="filter-getriebe-simple"
        value={value.length ? value[0].toString() : "0"}
        label={t({
          id: "filter.getriebe.label",
          defaultMessage: "Getriebe",
        })}
        onChange={setValueHandler}
      >
        <MenuItem value={0}>{all.title}</MenuItem>
        {options.map(({ value, title }) => (
          <MenuItem key={"getriebe-" + value} value={value}>
            {title}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

interface IContainer {
  Pipe?: any;
  variant?: string;
}

const Container: React.FC<IContainer> = ({
  Pipe = Pipeline,
  variant = "side",
}) => {
  const Filter = variant === "side" ? SideFilter : SimpleFilter;
  return (
    <Pipe
      {...{
        type: "getriebe",
        defaultValue: [],
        Component: Filter,
        Reducer: GetriebeReducer,
        Indicator: GetriebeIndicator,
      }}
    />
  );
};

export default Container;
